import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource } from '@angular/material/table';
import { SharedModule } from 'src/app/shared/shared.module';
import { CreateEditTableComponent } from 'src/app/shared/components/tables/create-edit-table/create-edit-table.component';
import { ActionIcons, actionText, apiCrudActions, BulkDeleteItemNames, ModalPopupMessages, ModeIcons, pageType, popupWidth, RoutingMessages, SingleDeleteHeaderText, StatusCodes } from 'src/app/shared/utility/constants';
import {
  RoutingCriteriaRow,
  routingCriteriaGridColumns,
  routingCriteriaFormConfig,
} from '../../accounts.config';
import { SvgIconProp } from 'src/app/shared/models/dynamic.model';
import { DefaultSelectTableHeaderIcons } from 'src/app/shared/utility/svg-constants';
import { CommonService } from 'src/app/shared/services/common.service';
import { Observable, Subscription } from 'rxjs';
import { SnakbarService } from 'src/app/shared/services/snakbar.service';
import { FormGroup } from '@angular/forms';
import { AccountsService } from 'src/app/features/service/accounts.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { MessagePopupComponent } from 'src/app/shared/components/message-popup/message-popup.component';
import { Store } from '@ngxs/store';
import { InformationNoteComponent } from 'src/app/shared/components/information-note/information-note.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IGovernmentInfoDetailsData } from 'src/app/features/model/sm-accounts';

@Component({
  selector: 'app-routing',
  standalone: true,
  imports: [CommonModule, SharedModule, CreateEditTableComponent, InformationNoteComponent],
  templateUrl: './routing.component.html',
  styleUrl: './routing.component.scss',
})
export class RoutingComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  public routingTableDataSource = new MatTableDataSource<RoutingCriteriaRow>(
    []
  );
  public routingTableData: RoutingCriteriaRow[] = [];
  public routingCustomizeColumnsConfig = routingCriteriaGridColumns;
  public routingFormConfig = routingCriteriaFormConfig;
  public tableName = pageType;
  public routingTableHeaderIcons: SvgIconProp[] = structuredClone(
    DefaultSelectTableHeaderIcons
  ).filter(icon => icon.name !== 'view');
  public pageMode!: string;
  private routingFormValueSub?: Subscription;
  public iconTabEvent = '';
  private routingSelectedRows: any;
  public routingGridForm!: FormGroup;
  private rowBeingEdited: any = null;
  @ViewChild(CreateEditTableComponent)
  private readonly createEditTableComp!: CreateEditTableComponent;
  private editingContext: {
    criteriaId?: number;
    tempId?: string;
    serviceTypeId?: string;
    modeOfTransportId?: number | null;
    modeOfTransportType?: string | null;
    shipmentTypeId?: number | null;
  } | null = null;
  public selectedGovtServiceTypes: string[] = [];
  public showServiceTypeWarning = false;
  private governmentInfoData$!: Observable<IGovernmentInfoDetailsData>;
  public chooseServiceTypeBeforeAddingRoutingCriteria = RoutingMessages.chooseServiceTypeBeforeAddingRoutingCriteria;
  public modeIcons = ModeIcons;

  constructor(
    public readonly commonService: CommonService,
    private readonly snakbarService: SnakbarService,
    private readonly accountsService: AccountsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly modalService: ModalService,
    private readonly store: Store
  ) { }

  ngOnInit(): void {
    this.routingTableData = [];
    this.routingTableDataSource.data = this.routingTableData;
    this.checkPageMode();
    this.loadExistingRoutingCriteriaFromService();
    this.subscribeToRoutingDataFromAPI();
    this.subscribeToGovernmentInfoServiceTypes();
  }

  private subscribeToGovernmentInfoServiceTypes(): void {
    this.governmentInfoData$ = this.store.select(state => state.accountsGovernmentInfo);
    this.governmentInfoData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((govtInfo: IGovernmentInfoDetailsData) => {
        if (govtInfo?.serviceTypes && Array.isArray(govtInfo.serviceTypes)) {
          this.selectedGovtServiceTypes = govtInfo.serviceTypes;
          this.showServiceTypeWarning = this.selectedGovtServiceTypes.length === 0;
        } else {
          this.selectedGovtServiceTypes = [];
          this.showServiceTypeWarning = true;
        }
        this.cdr.detectChanges();
      });
  }

  private hasAccountId(): boolean {
    const accountId = localStorage.getItem('accountId');
    return !!accountId && accountId !== '' && accountId !== '0' && accountId !== 'null';
  }

  private loadExistingRoutingCriteriaFromService(): void {
    const existingData = this.accountsService
      .getRoutingCriteria()
      .filter((item: any) => item.tempId && !item.criteriaId);
    if (existingData.length > 0 && !this.hasAccountId()) {
      const gridData = existingData.map((item: any) => ({
        serviceType: item.serviceTypeName || item.serviceType,
        shipmentValueMin: item.minShipmentValue?.toString() || '',
        shipmentValueMax: item.maxShipmentValue?.toString() || '',
        shipmentWeightMin: item.minShipmentWeight?.toString() || '',
        shipmentWeightMax: item.maxShipmentWeight?.toString() || '',
        modeOfTransport: item.modeOfTransportName || '',
        shipmentType: item.shipmentTypeName || '',
        isInputRow: false,
        tempId: item.tempId,
        serviceTypeId: item.serviceType,
        modeOfTransportId: item.modeOfTransport,
        modeOfTransportType: item.modeOfTransportType,
        shipmentTypeId: item.shipmentType,
      }));

      if (this.pageMode === ModeIcons.add || this.pageMode === ModeIcons.edit) {
        this.routingTableData = [this.createInputRow(), ...gridData];
      } else {
        this.routingTableData = gridData;
      }
      this.routingTableDataSource.data = this.routingTableData;
    }
  }

  private createInputRow(): RoutingCriteriaRow {
    return {
      serviceType: '',
      shipmentValueMin: '',
      shipmentValueMax: '',
      shipmentWeightMin: '',
      shipmentWeightMax: '',
      modeOfTransport: '',
      shipmentType: '',
      isInputRow: true,
    };
  }

  private checkPageMode() {
    this.commonService.isCheckForMode
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (data === ModeIcons.view) {
          this.pageMode = ModeIcons.view;
        } else {
          this.pageMode = data === '' ? ModeIcons.add : data;
        }

        if ((this.pageMode === ModeIcons.add || this.pageMode === ModeIcons.edit) &&
          this.routingTableData.length === 0) {
          const inputRow = this.createInputRow();
          this.routingTableData = [inputRow];
          this.routingTableDataSource.data = this.routingTableData;
        }
        this.updateRoutingHeaderIcons(this.routingSelectedRows?.length || 0);
      });

  }

  public onRoutingIconTabClicked(event: any) {
    this.iconTabEvent = event.name;
    this.handleRoutingIconTab();
    this.iconTabEvent = '';
  }

  private handleRoutingIconTab() {
    if (this.iconTabEvent === ActionIcons.add) {
      this.handleRoutingAdd();
    } else if (this.iconTabEvent === ActionIcons.save) {
      this.handleRoutingSave();
    } else if (this.iconTabEvent === ActionIcons.close) {
      this.routingCloseClicked();
    } else if (this.iconTabEvent === ActionIcons.delete) {
      this.handleRoutingDelete();
    } else if (this.iconTabEvent === ActionIcons.edit) {
      this.handleRoutingEdit();
    }
  }
  private handleRoutingAdd(): void {
    if (!this.hasInputRow()) {
      const inputRow = this.createInputRow();
      this.routingTableDataSource.data = [inputRow, ...this.routingTableDataSource.data];
      this.routingTableData = this.routingTableDataSource.data;
      this.updateRoutingHeaderIcons(0);
    }
  }

  private routingCloseClicked() {
    if (this.rowBeingEdited) {
      const dataWithoutInputRow = this.routingTableDataSource.data.filter(
        (row: any) => !row.isInputRow
      );
      if (this.pageMode === ModeIcons.add || this.pageMode === ModeIcons.edit) {
        this.routingTableDataSource.data = [
          this.createInputRow(),
          ...dataWithoutInputRow,
          this.rowBeingEdited,
        ];
      } else {
        this.routingTableDataSource.data = [...dataWithoutInputRow, this.rowBeingEdited];
      }
      this.rowBeingEdited = null;
      this.editingContext = null;
    } else if (this.pageMode === ModeIcons.view) {
      this.routingTableDataSource.data = this.routingTableDataSource.data.filter(
        (row: any) => !row.isInputRow
      );
    }
    this.routingTableData = this.routingTableDataSource.data;
    this.clearTableSelection();
    if (this.pageMode === ModeIcons.view) {
      this.snakbarService.openCustomisedSnackBar(
        ModalPopupMessages.actionCancel,
        ModalPopupMessages.success
      );
    }
    this.routingDataReset();
  }

  private updateRoutingHeaderIcons(selectedCount: number = 0): void {
    const hasInputRow = this.hasInputRow();

    this.routingTableHeaderIcons = this.routingTableHeaderIcons.map(icon => {

      if (selectedCount > 0) {
        switch (icon.name) {
          case ActionIcons.edit:
            icon.isDisabled = selectedCount !== 1;
            break;

          case ActionIcons.delete:
            icon.isDisabled = false;
            break;

          case ActionIcons.close:
            icon.isDisabled = true;
            break;

          case ActionIcons.add:
          case ActionIcons.save:
            icon.isDisabled = true;
            break;

          default:
            icon.isDisabled = true;
            break;
        }
      } else {
        switch (icon.name) {
          case ActionIcons.add:
            icon.isDisabled = hasInputRow;
            break;

          case ActionIcons.save:
            if (hasInputRow) {
              icon.isDisabled = !this.isRoutingFormValid();
            } else {
              icon.isDisabled = true;
            }
            break;

          case ActionIcons.close:
            icon.isDisabled = !hasInputRow;
            break;

          case ActionIcons.edit:
          case ActionIcons.delete:
            icon.isDisabled = true;
            break;

          default:
            break;
        }
      }
      return icon;
    });
  }

  private subscribeToRoutingDataFromAPI(): void {
    this.accountsService.isRoutingCriteriaFromAPI
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((routingData: any[]) => {
        if (this.hasAccountId()) {
          this.bindRoutingDataToGrid(routingData || []);
        }
      });
  }

  private bindRoutingDataToGrid(routingData: any[]): void {
    const gridData = (routingData || []).map((item: any) => ({
      serviceType: item.serviceTypeName || item.serviceType || '',
      shipmentValueMin: item.minShipmentValue?.toString() || '',
      shipmentValueMax: item.maxShipmentValue?.toString() || '',
      shipmentWeightMin: item.minShipmentWeight?.toString() || '',
      shipmentWeightMax: item.maxShipmentWeight?.toString() || '',
      modeOfTransport: item.modeOfTransportName || item.modeOfTransport || '',
      shipmentType: item.shipmentTypeName || item.shipmentType || '',
      isInputRow: false,
      criteriaId: item.criteriaId,
      serviceTypeId: item.serviceType,
      modeOfTransportId: item.modeOfTransportId,
      modeOfTransportType: item.modeOfTransportType,
      shipmentTypeId: item.shipmentType,
    }));

    if (this.pageMode === ModeIcons.add || this.pageMode === ModeIcons.edit) {
      this.routingTableData = [this.createInputRow(), ...gridData];
    } else {
      this.routingTableData = [...gridData];
    }

    this.routingTableDataSource.data = this.routingTableData;
    this.cdr.detectChanges();
  }

  private routingDataReset() {
    this.routingGridForm?.reset();
  }
  public onRoutingFormChange(form: FormGroup) {
    if (this.routingGridForm !== form) {
      this.routingFormValueSub?.unsubscribe();
      this.routingGridForm = form;
      this.routingFormValueSub = this.routingGridForm.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.updateRoutingHeaderIcons(this.routingSelectedRows?.length || 0);
      });
    }
  }

  private isRoutingFormValid(): boolean {
    return !!this.routingGridForm?.valid && !!this.routingGridForm?.get('serviceType')?.value;
  }

  public onRoutingTableSelectionChange(selectedRows: any[]) {
    this.routingSelectedRows = selectedRows;
    this.updateRoutingHeaderIcons(selectedRows.length);
  }

  private handleRoutingSave(): void {
    if (!this.routingGridForm?.get('serviceType')?.value) {
      this.snakbarService.openCustomisedSnackBar(
        ModalPopupMessages.serviceTypeErrorMsg,
        ModalPopupMessages.error
      );
      return;
    }
    if (this.routingGridForm.invalid) {
      return;
    }

    if (this.isDuplicateRoutingCriteria()) {
      this.snakbarService.openCustomisedSnackBar(
        ModalPopupMessages.routingAlreadyExist,
        ModalPopupMessages.error
      );
      return;
    }
    const routingData = this.buildRoutingCriteriaPayload()[0];

    if (this.pageMode === ModeIcons.view && this.hasAccountId()) {
      this.saveRoutingViaAPI(routingData);
    } else {
      this.saveRoutingToService(routingData);
    }
  }

  private saveRoutingToService(routingData: any): void {
    const criteriaId = routingData.criteriaId || this.editingContext?.criteriaId;
    const tempId = routingData.tempId || this.editingContext?.tempId;

    if (criteriaId) {
      routingData.criteriaId = criteriaId;
      routingData.action = apiCrudActions.update;
    } else if (tempId) {
      routingData.tempId = tempId;
      routingData.action = apiCrudActions.create;
    } else {
      routingData.tempId = `temp-${this.accountsService.generateUniqueTempId()}`;
      routingData.action = apiCrudActions.create;
    }
    routingData.serviceTypeName = this.routingGridForm.value.serviceType;
    routingData.modeOfTransportName = this.routingGridForm.value.modeOfTransport;
    routingData.shipmentTypeName = this.routingGridForm.value.shipmentType;

    this.accountsService.setRoutingCriteria(routingData);
    this.commonService.checkPristineAccordionData(false);

    if (criteriaId || this.editingContext?.criteriaId) {
      this.updateExistingRowInGrid(routingData);
    } else {
      this.mapGridAfterSuccessForAddMode(routingData);
    }
    this.rowBeingEdited = null;
    this.editingContext = null;
    this.routingDataReset();
  }

  private updateExistingRowInGrid(criteria: any): void {
    const formValue = this.routingGridForm.getRawValue();

    const updatedRow = {
      serviceType: criteria.serviceTypeName || formValue.serviceType,
      shipmentValueMin: formValue.shipmentValueMin?.toString() || '',
      shipmentValueMax: formValue.shipmentValueMax?.toString() || '',
      shipmentWeightMin: formValue.shipmentWeightMin?.toString() || '',
      shipmentWeightMax: formValue.shipmentWeightMax?.toString() || '',
      modeOfTransport: criteria.modeOfTransportName || formValue.modeOfTransport,
      shipmentType: criteria.shipmentTypeName || formValue.shipmentType,
      isInputRow: false,
      criteriaId: criteria.criteriaId || this.editingContext?.criteriaId,
      serviceTypeId: criteria.serviceType || this.editingContext?.serviceTypeId,
      modeOfTransportId: criteria.modeOfTransport || this.editingContext?.modeOfTransportId,
      modeOfTransportType: criteria.modeOfTransportType || this.editingContext?.modeOfTransportType,
      shipmentTypeId: criteria.shipmentType || this.editingContext?.shipmentTypeId,
    };

    const dataWithoutInputRow = this.routingTableDataSource.data.filter((r: any) => !r.isInputRow);

    const finalData = [...dataWithoutInputRow, updatedRow];

    if (this.pageMode === ModeIcons.add || this.pageMode === ModeIcons.edit) {
      this.routingTableDataSource.data = [this.createInputRow(), ...finalData];
    } else {
      this.routingTableDataSource.data = finalData;
    }

    this.routingTableData = this.routingTableDataSource.data;
    this.cdr.detectChanges();
  }

  private saveRoutingViaAPI(routingData: any): void {
    const criteriaId = routingData.criteriaId || this.editingContext?.criteriaId;
    const accountId = Number(localStorage.getItem('accountId') ?? 0);
    if (accountId === 0) {
      routingData.action = apiCrudActions.create;
      delete routingData.criteriaId;
    } else if (criteriaId) {
      routingData.criteriaId = criteriaId;
      routingData.action = apiCrudActions.update;
    } else {
      routingData.action = apiCrudActions.create;
    }
    const payload = {
      accountId,
      routingCriteria: [routingData],
    };

    this.accountsService
      .addorUpdateAccountRouting(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data.statusCode === StatusCodes.Success) {
            this.snakbarService.openCustomisedSnackBar(
              ModalPopupMessages.accountRouteAddUpdate(
                routingData.action === apiCrudActions.create
                  ? actionText.added
                  : actionText.update
              ),
              ModalPopupMessages.success
            );
            if (routingData.action === apiCrudActions.update) {
              this.updateExistingRowInGridAfterAPI(routingData);
            } else {
              this.mapGridAfterSuccess(routingData);
            }
            this.rowBeingEdited = null;
            this.editingContext = null;
            this.accountsService.setAccountFetchApi(true);
          } else if (data.statusCode === StatusCodes.SuccessfulRequest) {
            this.snakbarService.openCustomisedSnackBar(
              data.message,
              ModalPopupMessages.error
            );
          }
        },
      });
  }

  private mapGridAfterSuccessForAddMode(criteria: any): void {
    const updatedRow = {
      serviceType: criteria.serviceTypeName || this.routingGridForm.value.serviceType,
      shipmentValueMin: criteria.minShipmentValue,
      shipmentValueMax: criteria.maxShipmentValue,
      shipmentWeightMin: criteria.minShipmentWeight,
      shipmentWeightMax: criteria.maxShipmentWeight,
      modeOfTransport: criteria.modeOfTransportName || this.routingGridForm.value.modeOfTransport,
      shipmentType: criteria.shipmentTypeName || this.routingGridForm.value.shipmentType,
      isInputRow: false,
      tempId: criteria.tempId,
    };
    const existingRows = this.routingTableDataSource.data.filter(r => !r.isInputRow);
    this.routingTableDataSource.data = [
      this.createInputRow(),
      ...existingRows,
      updatedRow,
    ];
  }

  private updateExistingRowInGridAfterAPI(criteria: any): void {
    const formValue = this.routingGridForm.getRawValue();
    const updatedRow = {
      serviceType: formValue.serviceType,
      shipmentValueMin: formValue.shipmentValueMin?.toString() || '',
      shipmentValueMax: formValue.shipmentValueMax?.toString() || '',
      shipmentWeightMin: formValue.shipmentWeightMin?.toString() || '',
      shipmentWeightMax: formValue.shipmentWeightMax?.toString() || '',
      modeOfTransport: formValue.modeOfTransport,
      shipmentType: formValue.shipmentType,
      isInputRow: false,
      criteriaId: criteria.criteriaId || this.editingContext?.criteriaId,
      serviceTypeId: criteria.serviceType || this.editingContext?.serviceTypeId,
      modeOfTransportId: criteria.modeOfTransport || this.editingContext?.modeOfTransportId,
      modeOfTransportType: criteria.modeOfTransportType || this.editingContext?.modeOfTransportType,
      shipmentTypeId: criteria.shipmentType || this.editingContext?.shipmentTypeId,
    };

    const dataWithoutInputRow = this.routingTableDataSource.data.filter((r: any) => !r.isInputRow);
    const finalData = [...dataWithoutInputRow, updatedRow];

    this.routingTableDataSource.data = finalData;
    this.routingTableData = this.routingTableDataSource.data;
    this.routingDataReset();
    this.cdr.detectChanges();
  }

  private hasInputRow(): boolean {
    return this.routingTableDataSource.data?.some(
      (row: any) => row.isInputRow
    );
  }

  private buildRoutingCriteriaPayload(): any[] {
    const formValue = this.routingGridForm.getRawValue();

    const serviceTypeObj = this.getSelectedLookupItem(
      this.createEditTableComp?.routingServiceTypeField,
      formValue.serviceType
    );

    const shipmentTypeObj = this.getSelectedLookupItem(
      this.createEditTableComp?.routingShipmentTypeField,
      formValue.shipmentType
    );

    const modeOfTransportObj = this.getSelectedLookupItem(
      this.createEditTableComp?.routingModeOfTransportField,
      formValue.modeOfTransport
    );
    const serviceTypeId = serviceTypeObj?.id ?? this.editingContext?.serviceTypeId ?? '';
    const modeOfTransportId = modeOfTransportObj?.id ?? this.editingContext?.modeOfTransportId ?? null;
    const modeOfTransportType = modeOfTransportObj?.type ?? this.editingContext?.modeOfTransportType ?? null;
    const shipmentTypeId = shipmentTypeObj?.id ?? this.editingContext?.shipmentTypeId ?? null;
    const accountIdNum = Number(localStorage.getItem('accountId') ?? 0);
    /* New account: never send update. Existing account: update only with a server criteriaId. */
    const isUpdate =
      accountIdNum > 0 && !!this.editingContext?.criteriaId;
    const toNullable = (v: any) =>
      v === '' || v === null || v === undefined ? null : Number(v);
    return [
      {
        ...(accountIdNum > 0 &&
          this.editingContext?.criteriaId && {
            criteriaId: this.editingContext.criteriaId,
          }),
        ...(this.editingContext?.tempId && !this.editingContext?.criteriaId && { tempId: this.editingContext.tempId }),
        action: isUpdate ? apiCrudActions.update : apiCrudActions.create,
        serviceType: serviceTypeId,
        minShipmentValue: toNullable(formValue.shipmentValueMin),
        maxShipmentValue: toNullable(formValue.shipmentValueMax),
        minShipmentWeight: toNullable(formValue.shipmentWeightMin),
        maxShipmentWeight: toNullable(formValue.shipmentWeightMax),
        modeOfTransport: modeOfTransportId,
        modeOfTransportType: modeOfTransportType,
        shipmentType: shipmentTypeId,
      },
    ];
  }
  private mapGridAfterSuccess(criteria: any): void {
    const updatedRow = {
      serviceType: this.routingGridForm.value.serviceType,
      shipmentValueMin: criteria.minShipmentValue,
      shipmentValueMax: criteria.maxShipmentValue,
      shipmentWeightMin: criteria.minShipmentWeight,
      shipmentWeightMax: criteria.maxShipmentWeight,
      modeOfTransport: this.routingGridForm.value.modeOfTransport,
      shipmentType: this.routingGridForm.value.shipmentType,
      isInputRow: false,
      criteriaId: criteria.criteriaId ?? undefined,
    };

    this.routingTableDataSource.data = [
      ...this.routingTableDataSource.data.filter(r => !r.isInputRow),
      updatedRow,
    ];
  }
  private getSelectedLookupItem<T extends { id: number }>(
    list: T[] | undefined,
    selectedValue: string
  ): T | undefined {
    return list?.find((item: any) => item.name === selectedValue);
  }

  private handleRoutingEdit(): void {
    if (this.routingSelectedRows?.length === 1) {
      const selectedRow = this.routingSelectedRows[0];
      const fullRowData = this.routingTableDataSource.data.find((row: any) => {
        if (row.isInputRow) return false;
        if (selectedRow.criteriaId && row.criteriaId === selectedRow.criteriaId) return true;
        if (selectedRow.tempId && row.tempId === selectedRow.tempId) return true;

        return row.serviceType === selectedRow.serviceType &&
          row.shipmentValueMin === selectedRow.shipmentValueMin &&
          row.modeOfTransport === selectedRow.modeOfTransport;
      }) || selectedRow;
      this.rowBeingEdited = { ...selectedRow };
      this.editingContext = {
        criteriaId: fullRowData.criteriaId,
        tempId: fullRowData.tempId,
        serviceTypeId: fullRowData.serviceTypeId,
        modeOfTransportId: fullRowData.modeOfTransportId,
        modeOfTransportType: fullRowData.modeOfTransportType,
        shipmentTypeId: fullRowData.shipmentTypeId,
      };
      if (this.routingGridForm) {
        this.routingGridForm.patchValue({
          serviceType: fullRowData.serviceType,
          shipmentValueMin: fullRowData.shipmentValueMin,
          shipmentValueMax: fullRowData.shipmentValueMax,
          shipmentWeightMin: fullRowData.shipmentWeightMin,
          shipmentWeightMax: fullRowData.shipmentWeightMax,
          modeOfTransport: fullRowData.modeOfTransport,
          shipmentType: fullRowData.shipmentType
        });
      }
      const dataWithoutSelectedRow = this.routingTableDataSource.data.filter((row: any) => {
        if (row.isInputRow) return false;
        if (fullRowData.criteriaId) {
          return row.criteriaId !== fullRowData.criteriaId;
        }
        if (fullRowData.tempId) {
          return row.tempId !== fullRowData.tempId;
        }
        return true;
      });

      const inputRow = this.createInputRow();
      this.routingTableDataSource.data = [inputRow, ...dataWithoutSelectedRow];
      this.routingTableData = this.routingTableDataSource.data;
      this.clearTableSelection();
    }
  }

  private handleRoutingDelete(): void {
    if (!this.routingSelectedRows || this.routingSelectedRows.length === 0) {
      return;
    }
    const selectedCount = this.routingSelectedRows.length;
    let message: string;
    let messageType: string;

    if (selectedCount > 1) {
      message = ModalPopupMessages.bulkDeleteMessage(
        selectedCount,
        BulkDeleteItemNames.RoutingCriteria
      );
      messageType = ModalPopupMessages.BulkDeleteHeader;
    } else {
      const serviceType = this.routingSelectedRows[0]?.serviceType || '';
      message = ModalPopupMessages.singleDeleteMessage(
        SingleDeleteHeaderText.RoutingCriteria
      ) + (serviceType ? `record ${serviceType}?` : 'record?');
      messageType = ModalPopupMessages.singleDeleteHeader(
        SingleDeleteHeaderText.RoutingCriteria
      );
    }
    const dialogRef = this.modalService.openPopup(
      {
        message: message,
        isMultiple: true,
        isFooterRequired: true,
        isBulk: selectedCount > 1,
      },
      messageType,
      MessagePopupComponent,
      popupWidth.deletePopup
    );

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.deleteRoutingCriteria();
      }
    });
  }

  private deleteRoutingCriteria(): void {
    const accountId = localStorage.getItem('accountId');
    if (!accountId) {
      this.deleteRoutingFromLocalData();
      return;
    }

    const criteriaIds = this.routingSelectedRows
      .filter((row: any) => row.criteriaId)
      .map((row: any) => row.criteriaId);

    if (criteriaIds.length === 0) {
      this.deleteRoutingFromLocalData();
      return;
    }

    const payload = {
      accountId: +accountId,
      criteriaIds: criteriaIds,
    };

    this.accountsService
      .deleteAccountRouting(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: any) => {
          if (data.statusCode === StatusCodes.Success) {
            this.snakbarService.openCustomisedSnackBar(
              ModalPopupMessages.accountRouteAddUpdate(actionText.deleted),
              ModalPopupMessages.success
            );
            this.clearTableSelection();
            this.accountsService.setAccountFetchApi(true);
          }
        },
      });
  }

  private deleteRoutingFromLocalData(): void {
    const selectedTempIds = this.routingSelectedRows
      .filter((row: any) => row.tempId)
      .map((row: any) => row.tempId);

    selectedTempIds.forEach((tempId: string) => {
      this.accountsService.removeRoutingCriteria(tempId);
    });

    this.routingTableDataSource.data = this.routingTableDataSource.data.filter(
      (row: any) => !this.routingSelectedRows.includes(row)
    );
    this.routingTableData = this.routingTableDataSource.data;
    this.clearTableSelection();
  }

  private clearTableSelection(): void {
    if (this.createEditTableComp) {
      this.createEditTableComp.selection?.clear();
    }
    this.routingSelectedRows = [];
    this.updateRoutingHeaderIcons(0);
  }

  private isDuplicateRoutingCriteria(): boolean {
    const formValue = this.routingGridForm.getRawValue();
    const existingRows = this.routingTableDataSource.data.filter((row: any) => {
      if (row.isInputRow) return false;

      if (this.editingContext?.criteriaId && row.criteriaId === this.editingContext.criteriaId) {
        return false;
      }
      if (this.editingContext?.tempId && row.tempId === this.editingContext.tempId) {
        return false;
      }

      return true;
    });
    return existingRows.some((row: any) => {
      const isSameServiceType = row.serviceType === formValue.serviceType;
      const isSameShipmentValueMin = row.shipmentValueMin === formValue.shipmentValueMin;
      const isSameShipmentValueMax = row.shipmentValueMax === formValue.shipmentValueMax;
      const isSameShipmentWeightMin = row.shipmentWeightMin === formValue.shipmentWeightMin;
      const isSameShipmentWeightMax = row.shipmentWeightMax === formValue.shipmentWeightMax;
      const isSameModeOfTransport = row.modeOfTransport === formValue.modeOfTransport;
      const isSameShipmentType = row.shipmentType === formValue.shipmentType;
      return isSameServiceType &&
        isSameShipmentValueMin &&
        isSameShipmentValueMax &&
        isSameShipmentWeightMin &&
        isSameShipmentWeightMax &&
        isSameModeOfTransport &&
        isSameShipmentType;
    });
  }
}